import { eventBus } from '@atc/cai-event-bus';
import { getPixallId } from '@atc/pixall-fns';
import { getPath, WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST } from 'wallet-manager';

export default async function saveDeal({
    componentName = '',
    consumerBrand,
    deal,
    saveDealXgFetcher,
    env, // env: 'prod' or 'staging'
    dealXgId,
    formData = {}, // email only
    icoTradeIn,
    isEsntial = false,
    isScheduleTestDrive = false, // email only
    listing = {},
    pixallId,
    phoneNumber, // email only
    walletId,
    zip,
}) {
    const {
        owner: { id: ownerId } = {},
        make = '',
        model = '',
        trim = '',
        type = 'used',
        year,
        vin,
        specifications = {},
        id: listingId,
        images,
        paymentServices: { dealerSettings } = {},
        marketExtension: { parentOwnerId, isVirtualDealer } = {},
        pricingDetail: { msrp = '' } = {},
    } = listing;

    const dealXGOwnerId = isEsntial && isVirtualDealer && !!parentOwnerId ? parentOwnerId : ownerId;

    const hasInvalidSalePrice = deal?.salePrice === '';
    const hasInvalidTerms = deal?.dealType === 'finance' && deal?.terms === '';
    const hasInvalidPayment =
        (deal?.dealType === 'lease' || deal?.dealType === 'finance') &&
        (deal?.payment === 'N/A' || deal?.payment === '');

    const hasInvalidDeal = !dealXGOwnerId || hasInvalidSalePrice || hasInvalidTerms || hasInvalidPayment;
    const hasInvalidDealType = !['cash', 'finance', 'lease'].includes(deal?.dealType);
    if (hasInvalidDealType) {
        deal = {
            ...deal,
            dealType: 'finance',
        };
    }

    if (icoTradeIn) {
        deal = {
            ...deal,
            ico: {
                value: icoTradeIn?.offerId,
                createdByDateTime: icoTradeIn?.createdDate,
                createdByClientId: consumerBrand === 'AT' ? 'ATC' : consumerBrand,
            },
        };
    }
    let mmd = dealerSettings?.mmdId ?? null;
    if (componentName === 'EsntialBuyNowCTA') {
        mmd = null;
    }

    if (!pixallId) {
        try {
            pixallId = getPixallId();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log('getPixall() failed with the following error: ', error);
            pixallId = null;
        }
    }

    const payloadData = {
        carId: listingId,
        deal,
        mmd,
        ownerId: dealXGOwnerId,
        pixallId,
        vehicle: {
            make: make?.name ?? make,
            model: model?.name ?? model,
            trim: trim?.name ?? trim,
            listingType: type,
            year,
            vin,
            mileage: specifications.mileage?.value?.replace(/,/g, ''),
            imageUrl: images?.sources?.[0]?.src ?? '',
            msrp: type === 'New' ? msrp.toString() : '',
        },
        walletId,
        // following payload data is for email only
        comments: formData.comments,
        firstName: formData.firstName,
        fromEmailAddress: formData.fromEmailAddress,
        lastName: formData.lastName,
        phoneNumber,
        ...(isScheduleTestDrive &&
            formData.date && {
                testDrive: { scheduledDate: new Date(`${formData.date} ${formData?.time}`).toISOString() },
            }),
        zip,
        isEsntial,
    };

    let responseDealXgId;
    let statusDescription;

    const hosts = {
        prod: 'www.kbb.com',
        staging: 'staging.kbb.com',
    };

    const host = getPath(env, hosts);

    if (!hasInvalidDeal && deal) {
        ({ id: responseDealXgId, statusDescription } = (await saveDealXgFetcher(host, payloadData, dealXgId)) || {});
    }

    // Emit regardless of fetcher response
    // An edge case can happen where the deal is ultimately created but
    // the proxy response times out before it receives confirmation
    eventBus.emit(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, { walletId, dealXgId: responseDealXgId });

    if (responseDealXgId && typeof responseDealXgId === 'string') {
        return responseDealXgId;
    }

    // eslint-disable-next-line no-console
    console.log('saving the deal to DealXg failed due to:', statusDescription);
    if (window && window.newrelic) {
        window.newrelic.addPageAction('DealSubmitError', {
            id: listingId,
            component: componentName,
            message: `saving the deal to DealXg failed due to: ${statusDescription}`,
        });
    }
    if (hasInvalidDeal && window && window.newrelic) {
        const missingData = [];
        if (!dealXGOwnerId) {
            missingData.push('ownerId');
        }
        if (hasInvalidTerms) {
            missingData.push('terms');
        }
        if (hasInvalidPayment) {
            missingData.push('payment');
        }
        window.newrelic.addPageAction('DealSubmitError', {
            id: listingId,
            component: componentName,
            message: `skipped deal creation due to missing deal data: missing ${missingData.join(', ')}.`,
        });
    }

    return null;
}
